import { BehaviorSubject, EMPTY, Observable, from, fromEventPattern, Subject, firstValueFrom, of } from 'rxjs';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import {
    HubMessage,
    RecruitingHubMessage,
    REALTIME_UPDATES_ERROR_MESSAGE,
    CORE_HUB_PATH,
    InAppNotificationMessage,
    PdHubMessage,
    PD_HUB_PATH,
    PD_UPDATES_ERROR_MESSAGE,
    AccountManagerHubMessage,
    TIMECARD_HUB_PATH,
    TimecardHubMessage,
    INVOICE_HUB_PATH,
    REVERSE_INVOICE_HUB_PATH,
    InvoiceHubMessage,
    ReverseInvoiceHubMessage,
    COMPLIANCE_REPORT_HUB_PATH,
    ComplianceReportHubMessage
} from './realtime-constants';
import {
    catchError,
    delay,
    first,
    map,
    mergeMap,
    retry,
    retryWhen,
    switchMap,
    take,
    takeUntil,
    tap
} from 'rxjs/operators';

import { Inject, Injectable } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { IdentityService } from 'src/app/core/identity.service';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { LDFeatureManager } from './ld-feature-manager';
import { FeatureFlag } from '../models/enums/feature-flag';

interface HubConfig {
    path: string;
    messageRegistrations: string[];
    onClose: (err: Error | undefined) => void;
    onStartupError: (err: Error) => void;
}

class RealtimeConnection {
    private _hubConnection: HubConnection;
    private _groups: string[] = [];
    private _connectionClosed = new Subject();

    private messages: {
        [key: string]: Subject<any>;
    } = {};

    private readonly canDisconnect = new BehaviorSubject<boolean>(false);

    constructor(
        private readonly _hubConfig: HubConfig,
        private readonly _accessToken: Observable<string>,
        private readonly _coreApiUrl: string,
        private readonly _ldFeatureManager: LDFeatureManager
    ) {
        //set the initial subject for each message type. These will be subscribed to by ngrx effects.
        this._hubConfig.messageRegistrations.forEach((message) => (this.messages[message] = new Subject<any>()));
    }

    messagesOf<T>(messageName: string): Observable<T> {
        return this.messages[messageName].asObservable();
    }

    startConnection(groups: string[] = []): Observable<void> {
        return this._ldFeatureManager.isEnabled(FeatureFlag.PreventDuplicateSignalRConnections).pipe(
            take(1),
            switchMap((isEnabled) => {
                if (!isEnabled) {
                    return this.startConnectionOld(groups);
                }

                this._groups = [...new Set([...this._groups, ...groups])];

                return this.canDisconnect.pipe(
                    take(1), // only want current state of connection
                    switchMap((canDisconnect) => {
                        if (canDisconnect) {
                            return of(undefined); // currently connected/connecting; don't reinitialize
                        }

                        // create new connection
                        this.initializeHubConnection();
                        return from(this._hubConnection.start());
                    }),
                    mergeMap(
                        () => from(Promise.all(groups.map((g) => this._hubConnection.invoke(HubMessage.JOIN_GROUP, g)))) // join groups
                    ),
                    tap(() => {
                        this.canDisconnect.next(true); // announce connected
                    }),
                    retry({ count: 10, delay: 3000 }),
                    catchError((err) => {
                        this.canDisconnect.next(false);
                        this._hubConfig.onStartupError(err);
                        return EMPTY;
                    })
                );
            }),
            map((_) => _ as void) // map from 'any' to 'void'
        );
    }

    startConnectionOld(groups: string[] = []): Observable<any> {
        this.initializeHubConnection();
        this._groups = groups;
        const maxAttempts = 10;
        let attempts = 0;

        this.canDisconnect.next(false);
        return from(this._hubConnection.start()).pipe(
            mergeMap(() => from(Promise.all(groups.map((g) => this._hubConnection.invoke(HubMessage.JOIN_GROUP, g))))),
            tap(() => {
                this.canDisconnect.next(true);
            }),
            retryWhen((errors) =>
                errors.pipe(
                    tap((error) => {
                        if (attempts >= maxAttempts) {
                            throw error;
                        } else {
                            attempts++;
                        }
                    }),
                    delay(3000) // wait a few seconds before trying again
                )
            ),
            catchError((err) => {
                this._hubConfig.onStartupError(err);
                return EMPTY;
            })
        );
    }

    stopConnection(): Observable<void> {
        if (
            !this._hubConnection ||
            this._hubConnection.state === HubConnectionState.Disconnected ||
            this._hubConnection.state === HubConnectionState.Disconnecting
        ) {
            return EMPTY;
        }

        // canDisconnect prevents us from disconnecting before a previous connection has been completed
        return this.canDisconnect.pipe(
            first((c) => c),
            mergeMap(() =>
                from(Promise.all(this._groups.map((g) => this._hubConnection.invoke(HubMessage.LEAVE_GROUP, g))))
            ),
            mergeMap(() => from(this._hubConnection.stop())),
            tap(() => {
                this._connectionClosed.next(true);
                console.log(`Hub connection stopped: ${this._hubConfig.path}`);
            })
        );
    }

    isConnectionActive(): Observable<boolean> {
        return this.canDisconnect;
    }

    private initializeHubConnection() {
        this._hubConnection = new HubConnectionBuilder()
            .configureLogging(LogLevel.Warning)
            .withUrl(`${this._coreApiUrl}${this._hubConfig.path}`, {
                // Once we're on rxjs 7, swap out the following line.
                accessTokenFactory: () => firstValueFrom(this._accessToken.pipe(take(1)))
            })
            .withAutomaticReconnect()
            // https://docs.microsoft.com/en-us/aspnet/core/signalr/configuration?view=aspnetcore-5.0&tabs=dotnet#configure-logging
            // .configureLogging(LogLevel.Debug)
            .build();

        this.registerEvents();
        this.registerClose();
        this.registerReconnection();
    }

    private registerClose(): void {
        this._hubConnection.onclose((err) => {
            this.canDisconnect.next(false);
            this._hubConfig.onClose(err);
        });
    }

    private registerEvents(): void {
        //when an signal R event is triggered, emit the payload from the corresponding message subject
        //this pattern keeps the initial observable that ngrx subscribed to in tact while allowing change out the hub events when a connection is started
        this._hubConfig.messageRegistrations.forEach((message) =>
            fromEventPattern(
                (handler) => this._hubConnection.on(message, handler),
                (handler) => this._hubConnection.off(message, handler)
            )
                .pipe(map((d) => (this.messages[message] as Subject<unknown>).next(d)))
                .pipe(takeUntil(this._connectionClosed))
                .subscribe()
        );
    }

    private registerReconnection(): void {
        this._hubConnection.onreconnected(() => this.canDisconnect.next(true));
        this._hubConnection.onreconnecting(() => this.canDisconnect.next(false));
    }
}

@Injectable({
    providedIn: 'root'
})
export class HubConnectionService {
    get coreHub(): RealtimeConnection {
        this._coreHub =
            this._coreHub ??
            new RealtimeConnection(
                {
                    path: CORE_HUB_PATH,
                    messageRegistrations: [
                        RecruitingHubMessage.JOBS_UPDATED,
                        RecruitingHubMessage.LEAD_ASSIGNED,
                        RecruitingHubMessage.RECRUITING_DASHBOARD_ALERT,
                        RecruitingHubMessage.RECRUITING_DASHBOARD_JOB_ALERT,
                        RecruitingHubMessage.RECRUITING_DEAL_ALERT,
                        InAppNotificationMessage.SENT,
                        AccountManagerHubMessage.ACCOUNT_MANAGER_DASHBOARD_ALERT
                    ],
                    onClose: this.defaultErrorCallback,
                    onStartupError: this.defaultErrorCallback
                },
                this._identity?.coreAccessToken$,
                this._settings.coreAPIUrl,
                this._ldFeatureManager
            );
        return this._coreHub;
    }

    get pdHub(): RealtimeConnection {
        this._pdHub =
            this._pdHub ??
            new RealtimeConnection(
                {
                    path: PD_HUB_PATH,
                    messageRegistrations: [
                        PdHubMessage.RATES_UPDATED,
                        PdHubMessage.INCENTIVE_RATES_UPDATED,
                        PdHubMessage.INCENTIVE_RATES_UPDATING,
                        PdHubMessage.RATES_UPDATING
                    ],
                    onClose: this.pdErrorCallback,
                    onStartupError: this.pdErrorCallback
                },
                this._identity?.coreAccessToken$,
                this._settings.coreAPIUrl,
                this._ldFeatureManager
            );
        return this._pdHub;
    }

    get timecardHub(): RealtimeConnection {
        this._timecardHub =
            this._timecardHub ??
            new RealtimeConnection(
                {
                    path: TIMECARD_HUB_PATH,
                    messageRegistrations: [
                        TimecardHubMessage.TIMECARD_SYNC_START,
                        TimecardHubMessage.TIMECARD_SYNC_SUCCESS,
                        TimecardHubMessage.TIMECARD_SYNC_FAILURE
                    ],
                    onClose: this.defaultErrorCallback,
                    onStartupError: this.defaultErrorCallback
                },
                this._identity?.coreAccessToken$,
                this._settings.coreAPIUrl,
                this._ldFeatureManager
            );

        return this._timecardHub;
    }

    get invoiceHub(): RealtimeConnection {
        this._invoiceHub =
            this._invoiceHub ??
            new RealtimeConnection(
                {
                    path: INVOICE_HUB_PATH,
                    messageRegistrations: [
                        InvoiceHubMessage.INVOICE_PROCESS_SUCCESS,
                        InvoiceHubMessage.INVOICE_PROCESS_FAILURE
                    ],
                    onClose: this.defaultErrorCallback,
                    onStartupError: this.defaultErrorCallback
                },
                this._identity?.coreAccessToken$,
                this._settings.coreAPIUrl,
                this._ldFeatureManager
            );

        return this._invoiceHub;
    }

    get reverseInvoiceHub(): RealtimeConnection {
        this._reverseInvoiceHub =
            this._reverseInvoiceHub ??
            new RealtimeConnection(
                {
                    path: REVERSE_INVOICE_HUB_PATH,
                    messageRegistrations: [
                        ReverseInvoiceHubMessage.REVERSE_INVOICE_PROCESS_SUCCESS,
                        ReverseInvoiceHubMessage.REVERSE_INVOICE_PROCESS_FAILURE
                    ],
                    onClose: this.defaultErrorCallback,
                    onStartupError: this.defaultErrorCallback
                },
                this._identity?.coreAccessToken$,
                this._settings.coreAPIUrl,
                this._ldFeatureManager
            );

        return this._reverseInvoiceHub;
    }

    get complianceReportHub(): RealtimeConnection {
        this._complianceReportHub =
            this._complianceReportHub ??
            new RealtimeConnection(
                {
                    path: COMPLIANCE_REPORT_HUB_PATH,
                    messageRegistrations: [ComplianceReportHubMessage.AUTOMATCH_RESPONSE],
                    onClose: this.defaultErrorCallback,
                    onStartupError: this.defaultErrorCallback
                },
                this._identity?.coreAccessToken$,
                this._settings.coreAPIUrl,
                this._ldFeatureManager
            );

        return this._complianceReportHub;
    }

    constructor(
        private readonly _snackbar: MatSnackBar,
        private readonly _identity: IdentityService,
        @Inject(APP_CONFIG) private readonly _settings: Settings,
        private readonly _ldFeatureManager: LDFeatureManager
    ) {}

    // Not sure if we're storing the token in the store in Nova atm, but doing so
    // and using a selector keeps the token refreshed as it changes.
    // private readonly accessToken = this.store.select(
    //     AuthSelectors.getAccessToken
    // );
    private _coreHub: RealtimeConnection | undefined;

    private _pdHub: RealtimeConnection | undefined;

    private _timecardHub?: RealtimeConnection;

    private _invoiceHub?: RealtimeConnection;

    private _reverseInvoiceHub?: RealtimeConnection;

    private _complianceReportHub?: RealtimeConnection;

    private readonly defaultErrorCallback = (err: Error | undefined) => {
        if (err) {
            this._snackbar.open(REALTIME_UPDATES_ERROR_MESSAGE);
        }
    };

    private readonly pdErrorCallback = (err: Error | undefined) => {
        if (err) {
            this._snackbar.open(PD_UPDATES_ERROR_MESSAGE);
        }
    };
}
